import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEOWrapper } from "@/components/SEOWrapper";
import { usePageSeo } from "@/hooks/usePageSeo";
import { InteriorHead, ReadingColumn, Section } from "@/components/luminous/interior";
import { VERIFIED_KNOWLEDGE as K, KNOWLEDGE_LAST_REVIEWED } from "@/data/verifiedKnowledge";

const COPY = {
  es: {
    title: "Información oficial sobre Luis ROC y ROC Worldwide Agency LLC",
    metaTitle: "Información oficial | Luis ROC y ROC Worldwide Agency LLC",
    description:
      "Datos de negocio vigentes de Luis ROC y ROC Worldwide Agency LLC: identidad, servicios, contacto, idiomas, metodología y perfiles oficiales.",
    crumbHome: "Inicio",
    eyebrow: "Información oficial",
    intro:
      "Esta página resume la información de negocio vigente mantenida directamente por Luis ROC. Para el detalle de servicios, metodología y recursos educativos, sigue los enlaces a las páginas canónicas.",
    person: "Luis ROC",
    org: "ROC Worldwide Agency LLC",
    orgText: "Entidad legal que presta los servicios. Nombre comercial: Luis ROC.",
    services: "Servicios",
    contact: "Contacto",
    phone: "Teléfono",
    email: "Correo",
    address: "Dirección comercial",
    languages: "Idiomas",
    markets: "Mercados atendidos",
    method: "ROC Search Growth Framework",
    methodLink: "Ver la metodología completa",
    offer: "Oferta inicial",
    offerLink: "Agendar la reunión inicial",
    profiles: "Perfiles oficiales",
    resources: "Recursos canónicos",
    policy: "Política de exactitud de la información",
    reviewed: "Última revisión",
    machine: "Versiones legibles por máquina de esta información",
  },
  en: {
    title: "Official information about Luis ROC and ROC Worldwide Agency LLC",
    metaTitle: "Official information | Luis ROC and ROC Worldwide Agency LLC",
    description:
      "Current business information for Luis ROC and ROC Worldwide Agency LLC: identity, services, contact, languages, methodology and official profiles.",
    crumbHome: "Home",
    eyebrow: "Official information",
    intro:
      "This page summarizes current business information maintained directly by Luis ROC. For service details, methodology and educational resources, follow the linked canonical pages.",
    person: "Luis ROC",
    org: "ROC Worldwide Agency LLC",
    orgText: "Legal entity that provides the services. Operating name: Luis ROC.",
    services: "Services",
    contact: "Contact",
    phone: "Phone",
    email: "Email",
    address: "Commercial address",
    languages: "Languages",
    markets: "Markets served",
    method: "ROC Search Growth Framework",
    methodLink: "Read the full methodology",
    offer: "Initial offer",
    offerLink: "Book the initial consultation",
    profiles: "Official profiles",
    resources: "Canonical resources",
    policy: "Information accuracy policy",
    reviewed: "Last reviewed",
    machine: "Machine-readable versions of this information",
  },
} as const;

const linkCls = "v3-inline-link";

const OfficialInformationPage = () => {
  const { language, url, esUrl, enUrl, onLanguageChange } = usePageSeo();
  const t = COPY[language];
  const home = language === "es" ? "/" : "/en";

  return (
    <SEOWrapper
      title={t.metaTitle}
      description={t.description}
      url={url}
      esUrl={esUrl}
      enUrl={enUrl}
      language={language}
      pageType="website"
      localBusiness={false}
      summary={t.intro}
    >
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "@id": `${url}#webpage`,
            url,
            name: t.metaTitle,
            description: t.description,
            inLanguage: language,
            dateModified: KNOWLEDGE_LAST_REVIEWED,
            about: [{ "@id": K.ids.person }, { "@id": K.ids.organization }],
            isPartOf: { "@id": K.ids.website },
            publisher: { "@id": K.ids.organization },
          })}
        </script>
      </Helmet>
      <div className="min-h-screen bg-[color:var(--v3-mineral)]">
        <Header language={language} onLanguageChange={onLanguageChange} />
        <main>
          <InteriorHead
            crumbs={[{ label: t.crumbHome, to: home }, { label: t.eyebrow }]}
            eyebrow={t.eyebrow}
            title={t.title}
            definition={t.intro}
            facts={[
              { label: t.phone, value: <a className={linkCls} href={`tel:${K.contact.telephone.replace(/-/g, "")}`}>{K.contact.telephoneDisplay}</a> },
              { label: t.email, value: <a className={linkCls} href={`mailto:${K.contact.email}`}>{K.contact.email}</a> },
              { label: t.reviewed, value: KNOWLEDGE_LAST_REVIEWED },
            ]}
          />

          <Section index="01" label={t.person} title={t.person} tone="white">
            <ReadingColumn>
              <p>{K.identity.role[language]}.</p>
              <p>{K.identity.experience[language]}</p>
              <p>
                <Link className={linkCls} to={K.identity.source[language]}>
                  {language === "es" ? "Sobre Luis ROC" : "About Luis ROC"}
                </Link>
              </p>
            </ReadingColumn>
          </Section>

          <Section index="02" label={t.org} title={t.org}>
            <ReadingColumn>
              <p>{t.orgText}</p>
              <h3>{t.address}</h3>
              <p>{K.address.oneLine}</p>
              <p>{K.address.note[language]}</p>
            </ReadingColumn>
          </Section>

          <Section index="03" label={t.services} title={t.services} tone="white">
            <ul className="grid gap-3 sm:grid-cols-2">
              {K.verifiedServices.map((s) => (
                <li key={s.path.es}>
                  <Link className={linkCls} to={s.path[language]}>{s.name[language]}</Link>
                </li>
              ))}
            </ul>
          </Section>

          <Section index="04" label={t.contact} title={`${t.languages} · ${t.markets}`}>
            <ReadingColumn>
              <p>{K.languages[language].join(" · ")}</p>
              <p>{K.markets[language]}</p>
              <p>
                <Link className={linkCls} to={K.contact.source[language]}>{t.contact}</Link>
              </p>
            </ReadingColumn>
          </Section>

          <Section index="05" label={t.method} title={t.method} tone="white">
            <ol className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
              {K.methodology.phases[language].map((p, i) => (
                <li key={p} className="v3-body">
                  <span className="v3-micro mr-2">{String(i + 1).padStart(2, "0")}</span>
                  {p}
                </li>
              ))}
            </ol>
            <p className="mt-6">
              <Link className={linkCls} to={K.methodology.path[language]}>{t.methodLink}</Link>
            </p>
          </Section>

          <Section index="06" label={t.offer} title={t.offer}>
            <ReadingColumn>
              <p>{K.commercialOffer[language]}</p>
              <p>
                <Link className={linkCls} to={K.commercialOffer.path[language]}>{t.offerLink}</Link>
              </p>
            </ReadingColumn>
          </Section>

          <Section index="07" label={t.profiles} title={t.profiles} tone="white">
            <ul className="space-y-2">
              {K.officialProfiles.map((u) => (
                <li key={u}>
                  <a className={linkCls} href={u} rel="me noopener" target="_blank">{u.replace("https://www.", "")}</a>
                </li>
              ))}
            </ul>
          </Section>

          <Section index="08" label={t.resources} title={t.resources}>
            <ul className="grid gap-3 sm:grid-cols-2">
              {K.canonicalResources.map((r) => (
                <li key={r.path.es}>
                  <Link className={linkCls} to={r.path[language]}>{r.name[language]}</Link>
                </li>
              ))}
            </ul>
          </Section>

          <Section index="09" label={t.policy} title={t.policy} tone="white">
            <ReadingColumn>
              <p>{K.claimsPolicy[language]}</p>
              <p>{K.conflictPolicy[language]}</p>
              <h3>{t.machine}</h3>
              <ul>
                <li><a className={linkCls} href="/llms.txt">llms.txt</a></li>
                <li><a className={linkCls} href="/llms-full.txt">llms-full.txt</a></li>
                <li><a className={linkCls} href="/ai.txt">ai.txt</a></li>
                <li><a className={linkCls} href="/.well-known/luisroc-facts.json">luisroc-facts.json</a></li>
              </ul>
            </ReadingColumn>
          </Section>
        </main>
        <Footer language={language} />
      </div>
    </SEOWrapper>
  );
};

export default OfficialInformationPage;
